import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isFeatureEnabled, getDisabledBehavior } from '../../lib/featureFlags'
import type { FlagKey } from '../../config/flags'

interface MegaHelinProps {
  lang: string
  onItemClick: () => void
}

interface HelinItem {
  /** Path under /:lang. */
  path: string
  /** i18n key for the label. */
  labelKey: string
  /** Optional flag - hidden if its disabled-behavior is `hide`. */
  flag?: FlagKey
}

const ABOUT: HelinItem[] = [
  { path: '/about', labelKey: 'megaHelin.items.about', flag: 'ABOUT' },
  { path: '/careers', labelKey: 'megaHelin.items.careers', flag: 'CAREERS' },
  { path: '/contact', labelKey: 'megaHelin.items.contact', flag: 'CONTACT' },
]

// Every item carries its page flag so an admin can hide it (Pages tab, "Hide").
// MegaHelin.test.tsx loops over every page flag to keep this complete.
const HELP: HelinItem[] = [
  { path: '/hair-length-guide', labelKey: 'megaHelin.items.hairGuide', flag: 'HAIR_LENGTH_GUIDE' },
  { path: '/gift-guide', labelKey: 'megaHelin.items.giftGuide', flag: 'GIFT_GUIDE' },
  { path: '/faq', labelKey: 'megaHelin.items.faq', flag: 'FAQ' },
]

const LEGAL: HelinItem[] = [
  { path: '/cancellation-policy', labelKey: 'megaHelin.items.cancellation', flag: 'CANCELLATION_POLICY' },
  { path: '/privacy', labelKey: 'megaHelin.items.privacy', flag: 'PRIVACY' },
  { path: '/cookies', labelKey: 'megaHelin.items.cookies', flag: 'COOKIES' },
  { path: '/photo-consent', labelKey: 'megaHelin.items.photoConsent', flag: 'PHOTO_CONSENT' },
]

function visible(item: HelinItem): boolean {
  if (!item.flag) return true
  if (isFeatureEnabled(item.flag)) return true
  return getDisabledBehavior(item.flag) !== 'hide'
}

export default function MegaHelin({ lang, onItemClick }: MegaHelinProps) {
  const { t } = useTranslation()

  const about = ABOUT.filter(visible)
  const help = HELP.filter(visible)
  const legal = LEGAL.filter(visible)

  return (
    <div className="grid grid-cols-12 gap-8">
      <Group titleKey="megaHelin.groupAbout" items={about} lang={lang} onItemClick={onItemClick} />
      <Group titleKey="megaHelin.groupHelp" items={help} lang={lang} onItemClick={onItemClick} />
      <Group titleKey="megaHelin.groupLegal" items={legal} lang={lang} onItemClick={onItemClick} />

      {/* Quiet trust line - same paragraph the footer uses */}
      <div className="col-span-12 md:col-span-3 md:pl-6 md:border-l border-ivory-dark">
        <h3 className="font-body font-semibold text-[10px] tracking-[0.25em] text-gold uppercase mb-4 pb-2 border-b border-gold/20">
          {t('megaHelin.trustEyebrow')}
        </h3>
        <p className="font-body text-sm text-mocha/70 leading-relaxed mb-2">
          {t('footerV3.trustParagraph')}<br />{t('footerV3.trustParagraph2')}
        </p>
        <p className="font-body text-[10px] tracking-[0.2em] text-mocha/35 uppercase">
          {t('footerV3.trustSignature')} · {t('footerV3.trustEst')}
        </p>
      </div>
    </div>
  )
}

function Group({
  titleKey,
  items,
  lang,
  onItemClick,
}: {
  titleKey: string
  items: HelinItem[]
  lang: string
  onItemClick: () => void
}) {
  const { t } = useTranslation()
  if (items.length === 0) return null
  return (
    <div className="col-span-6 md:col-span-3">
      <h3 className="font-body font-semibold text-[10px] tracking-[0.25em] text-gold uppercase mb-4 pb-2 border-b border-gold/20">
        {t(titleKey)}
      </h3>
      <ul className="space-y-2.5">
        {items.map((item) => (
          <li key={item.path}>
            <Link
              to={`/${lang}${item.path}`}
              onClick={onItemClick}
              className="block font-body text-sm text-mocha/80 hover:text-gold transition-colors"
            >
              {t(item.labelKey)}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
